import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import BackendUrlBanner from "./components/BackendUrlBanner";
import Home from "./pages/Home";
import RootGate from "./components/RootGate";
import Vehicles from "./pages/Vehicles";
import VehicleDetail from "./pages/VehicleDetail";
import Booking from "./pages/Booking";
import BookingSuccess from "./pages/BookingSuccess";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminBookings from "./pages/admin/Bookings";
import AdminFleet from "./pages/admin/Fleet";
import AdminBusiness from "./pages/admin/Business";
import AdminLocations from "./pages/admin/Locations";
import AdminUsers from "./pages/admin/Users";
import AdminCalendar from "./pages/admin/Calendar";
import AdminVouchers from "./pages/admin/Vouchers";
import AdminCarFleet from "./pages/admin/CarFleet";
import AdminRentalSettings from "./pages/admin/RentalSettings";
import AdminSystemSettings from "./pages/admin/SystemSettings";
import AdminCarExtras from "./pages/admin/CarExtras";
import AdminInquiries from "./pages/admin/Inquiries";
import AdminPriceRadar from "./pages/admin/PriceRadar";
import AdminFleetRadar from "./pages/admin/FleetRadar";
import AdminReports from "./pages/admin/Reports";
import AdminClients from "./pages/admin/Clients";
import PublicBook from "./pages/PublicBook";
import ManageBooking from "./pages/ManageBooking";
import VivaReturn from "./pages/VivaReturn";
import MokeDemo from "./pages/MokeDemo";

const STAFF_ROLES = ["admin", "super_admin", "staff", "editor"];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <BackendUrlBanner />
          <Routes>
            {/* Root is a gate — anon users bounce to /login, authed users to their console.
                The public marketing Home stays available at /preview/home behind auth
                so admins can still preview the customer landing UX. */}
            <Route path="/" element={<RootGate />} />
            {/* Fully public booking landing — NO admin/customer chrome, NO auth. */}
            <Route path="/book" element={<PublicBook />} />
            {/* Public self-service portal (v3.9.6). */}
            <Route path="/manage-booking" element={<ManageBooking />} />
            {/* v4.6.10 — alias used by the voucher boarding-pass QR code. */}
            <Route path="/my-booking" element={<ManageBooking />} />
            <Route path="/payment/viva/return" element={<VivaReturn />} />
            {/* Isolated lifestyle demo funnel (v4.0.4) — MOKE Experience. */}
            <Route path="/demo/moke" element={<MokeDemo />} />
            <Route path="/preview/home" element={<Layout><ProtectedRoute><Home /></ProtectedRoute></Layout>} />

            {/* Customer catalogue + booking — auth required (customers use this;
                admins access it as a Preview from the console). */}
            <Route path="/vehicles" element={<Layout><ProtectedRoute><Vehicles /></ProtectedRoute></Layout>} />
            <Route path="/vehicles/:id" element={<Layout><ProtectedRoute><VehicleDetail /></ProtectedRoute></Layout>} />
            <Route path="/booking/success" element={<Layout><ProtectedRoute><BookingSuccess /></ProtectedRoute></Layout>} />
            <Route path="/booking/:id" element={<Layout><ProtectedRoute><Booking /></ProtectedRoute></Layout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/dashboard" element={<Layout><ProtectedRoute><Dashboard /></ProtectedRoute></Layout>} />

            {/* Admin & Staff console */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout><AdminDashboard /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="bookings">
                  <AdminLayout><AdminBookings /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="clients">
                  <AdminLayout><AdminClients /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fleet"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="car_groups">
                  <AdminLayout><AdminFleet /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/business"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="business_settings">
                  <AdminLayout><AdminBusiness /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/system-settings"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="system_settings">
                  <AdminLayout><AdminSystemSettings /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/locations"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="locations">
                  <AdminLayout><AdminLocations /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <AdminLayout><AdminUsers /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/price-radar"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="price_radar">
                  <AdminLayout><AdminPriceRadar /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="reports">
                  <AdminLayout><AdminReports /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fleet-radar"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="fleet_radar">
                  <AdminLayout><AdminFleetRadar /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calendar"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="calendar">
                  <AdminLayout><AdminCalendar /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/vouchers"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="vouchers">
                  <AdminLayout><AdminVouchers /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/car-fleet"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="car_fleet">
                  <AdminLayout><AdminCarFleet /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rental-settings"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="rental_settings">
                  <AdminLayout><AdminRentalSettings /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/car-extras"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="car_extras">
                  <AdminLayout><AdminCarExtras /></AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inquiries"
              element={
                <ProtectedRoute allowedRoles={STAFF_ROLES} menu="inquiries">
                  <AdminLayout><AdminInquiries /></AdminLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
